import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import { useSelector } from "react-redux";
import axios from "axios";
import { getTheme } from "../theme/modernTheme";

const marquee = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.45; transform: scale(0.82); }
`;

const CURRENCY = {
  USD: "#15803d",
  EUR: "#1d4ed8",
  GBP: "#6d28d9",
  CHF: "#b91c1c",
  JPY: "#be185d",
  CNY: "#b45309",
  AUD: "#0e7490",
  CAD: "#c2410c",
  SAR: "#0f766e",
  RUB: "#4338ca",
};

const UP = "#059669";
const DOWN = "#e11d48";

function formatRate(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "";
  return v.toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
}

function formatDelta(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "";
  const abs = Math.abs(v).toLocaleString("tr-TR", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
  if (v > 0) return `+${abs}`;
  if (v < 0) return `-${abs}`;
  return abs;
}

function RateChip({ row, isDark }) {
  const color = CURRENCY[row.code] || "#334155";
  const up = row.direction === "up";
  const down = row.direction === "down";
  const dirColor = up ? UP : down ? DOWN : isDark ? "#94a3b8" : "#64748b";
  const unit = Number(row.unit) > 1 ? `${row.unit} ` : "";
  const arrow = up ? "▲" : down ? "▼" : "•";

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        mx: 0.6,
        px: 0.7,
        height: 24,
        borderRadius: "999px",
        background: isDark ? "rgba(255,255,255,0.05)" : "#fff",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}`,
        boxShadow: isDark ? "none" : "0 1px 0 rgba(15,23,42,0.04)",
        whiteSpace: "nowrap",
      }}
    >
      <Box
        component="span"
        sx={{
          px: 0.7,
          height: 16,
          lineHeight: "16px",
          borderRadius: "999px",
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: "0.04em",
          color,
          background: `${color}1f`,
        }}
      >
        {unit}
        {row.code}
      </Box>
      <Typography
        component="span"
        sx={{
          fontSize: 12,
          fontWeight: 750,
          lineHeight: "24px",
          fontVariantNumeric: "tabular-nums",
          color: isDark ? "#f8fafc" : "#0f172a",
        }}
      >
        {formatRate(row.forexSelling)}
      </Typography>
      {row.delta != null ? (
        <Typography
          component="span"
          sx={{
            fontSize: 11,
            fontWeight: 800,
            lineHeight: "24px",
            fontVariantNumeric: "tabular-nums",
            color: dirColor,
            pr: 0.3,
          }}
        >
          {arrow} {formatDelta(row.delta)}
        </Typography>
      ) : null}
    </Box>
  );
}

export default function TcmbRateTicker() {
  const mode = useSelector((state) => state.global.mode);
  const theme = getTheme(mode);
  const isDark = mode === "dark";
  const [rows, setRows] = useState([]);
  const [asOf, setAsOf] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get("/api/fx/live");
        if (cancelled || !data?.rows?.length) return;
        setRows(data.rows.filter((row) => CURRENCY[row.code]));
        setAsOf(data.asOf || "");
      } catch {
        /* şerit son başarılı kurda kalır */
      }
    };
    load();
    const timer = setInterval(load, 15000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  if (!rows.length) return null;

  const loop = [...rows, ...rows];

  return (
    <Box
      role="region"
      aria-label="Canlı piyasa kurları"
      sx={{
        display: "flex",
        alignItems: "center",
        height: 36,
        overflow: "hidden",
        borderBottom: `1px solid ${theme.colors.border.primary}`,
        background: isDark
          ? "linear-gradient(90deg, rgba(16,185,129,0.08), rgba(255,255,255,0.03) 18%, rgba(255,255,255,0.03))"
          : "linear-gradient(90deg, #f0fdf4 0%, #f8fafc 18%, #f8fafc 100%)",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.25,
          height: "100%",
          borderRight: `1px solid ${theme.colors.border.primary}`,
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: UP,
            boxShadow: `0 0 0 3px ${UP}33`,
            animation: `${pulse} 1.6s ease-in-out infinite`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
        <Typography
          component="span"
          sx={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.08em",
            color: UP,
            lineHeight: "36px",
          }}
        >
          CANLI
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: theme.colors.text.secondary,
            lineHeight: "36px",
          }}
        >
          PİYASA{asOf ? ` ${asOf}` : ""}
        </Typography>
      </Box>
      <Box sx={{ overflow: "hidden", flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "max-content",
            height: 36,
            animation: `${marquee} 70s linear infinite`,
            "&:hover": { animationPlayState: "paused" },
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        >
          {loop.map((row, i) => (
            <RateChip key={`${row.code}-${i}-${row.forexSelling}`} row={row} isDark={isDark} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
